import React, { useEffect, useState } from "react";

import date from "date-and-time";

import { PencilAltIcon, TrashIcon } from "@heroicons/react/outline";

import NoItems from "../../components/Shared/NoItems";
import { PageHeader } from "../../components/Shared/PageHeader";
import LoadingWheel from "../../components/Shared/LoadingWheel";
import ActionButton from "../../components/Shared/ActionButton";
import { IQRRedirect } from "../../models/qrRedirect/QRRedirect";
import { useQRRedirects } from "../../data/qrRedirect/useQRRedirects";
import { ButtonInput } from "../../components/Shared/Inputs/ButtonInput";
import ActionButtonContainer from "../../components/Shared/ActionButtonContainer";
import { useQRRedirectMutations } from "../../data/qrRedirect/useQRRedirectMutations";
import { CreateUpdateQRCodeFlyout } from "../../components/QRCode/CreateUpdateQRCodeRedirect";
import { useLocation } from "react-router-dom";

interface QRRedirectRowProps extends IQRRedirect {
  onEdit: () => void;
  onDelete: () => void;
}

function QRRedirectRow({ name, url, count, created, onEdit, onDelete }: QRRedirectRowProps) {
  return (
    <tr className="hover:bg-gray-100 hover:cursor-pointer" onClick={onEdit}>
      <td className="w-full max-w-0 py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:w-auto sm:max-w-none sm:pl-6">
        {name}
      </td>
      <td>
        {url.length > 180 ? `${url.slice(0, 180)}...` : url}
      </td>
      <td className="w-full max-w-0 py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:w-auto sm:max-w-none sm:pl-6">{count}</td>
      <td>
        {date.format(new Date(created), "DD/MM/YY HH:mm")}
      </td>
      <td className="py-4 pl-3 pr-4 text-sm font-medium sm:pr-6">
        <ActionButtonContainer>
          <ActionButton
            onClick={(e: React.MouseEvent) => {
              e.stopPropagation();
              onEdit();
            }}
            tooltip="Edit"
            Icon={PencilAltIcon}
          />
          <ActionButton
            onClick={(e: React.MouseEvent) => {
              e.stopPropagation();
              onDelete();
            }}
            tooltip="Delete"
            Icon={TrashIcon}
          />
        </ActionButtonContainer>
      </td>
    </tr>
  );
}

export default function QRCodeRedirectList() {
  const location = useLocation();
  const { qrRedirects } = useQRRedirects();
  const { remove } = useQRRedirectMutations();
  const [createQRCodeRedirectOpen, setCreateQRCodeRedirectOpen] = useState(false);
  const [selectedQRCode, setSelectedQRCode] = useState<IQRRedirect>();

  useEffect(() => {
    document.title = "EstateTrack | QR Codes";

    if (
      location.state &&
      (location.state as any).redirectId &&
      !qrRedirects.isFetching &&
      qrRedirects.data
    ) {
      const redirect = qrRedirects.data.data.find(
        qr => qr.id === (location.state as any).redirectId
      );

      if (redirect) {
        setSelectedQRCode(redirect);
        setCreateQRCodeRedirectOpen(true);

        // Clear location state to prevent repeated execution
        window.history.replaceState({}, document.title, location.pathname);
      }
    }
  }, [qrRedirects.isFetching, location]);

  const handleDeleteClick = (id: string) => remove.mutate(id);

  const handleCreateClose = () => {
    setCreateQRCodeRedirectOpen(false);
    selectedQRCode && setSelectedQRCode(undefined);
  }

  return <>
    <CreateUpdateQRCodeFlyout
      open={createQRCodeRedirectOpen}
      setOpen={handleCreateClose}
      qrCode={selectedQRCode}
    />
    <div className="px-4 sm:px-6 lg:px-8">
      <PageHeader title="QR Code Redirects" description="View and manage your QR Code redirects here">
        <ButtonInput
          label="Add QR Code"
          isSubmit={undefined}
          onClick={() => setCreateQRCodeRedirectOpen(true)}
          classes={undefined}
          children={undefined}
        />
      </PageHeader>
      <div className="-mx-4 mt-4 overflow-hidden shadow ring-1 ring-black ring-opacity-5 sm:-mx-6 md:mx-0 md:rounded-lg">
        {qrRedirects.isLoading ? (
          <div className="flex p-10 bg-white justify-center">
            <LoadingWheel width="w-10" height="w-10" />
          </div>
        ) : qrRedirects.data && qrRedirects.data.data.length > 0 ? (
          <table className="min-w-full divide-y divide-gray-300">
            <thead className="bg-gray-50">
              <tr>
                <th
                  scope="col"
                  className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6"
                >
                  Name
                </th>
                <th
                  scope="col"
                  className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6"
                >
                  URL
                </th>
                <th
                  scope="col"
                  className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6"
                >
                  Clicks
                </th>
                <th
                  scope="col"
                  className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6"
                >
                  Created
                </th>
                <th
                  scope="col"
                  className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6"
                >
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white">
              {qrRedirects.data.data.map((qr) => (
                <QRRedirectRow
                  key={qr.id}
                  onEdit={() => {
                    setSelectedQRCode(qr);
                    setCreateQRCodeRedirectOpen(true);
                  }}
                  onDelete={() => handleDeleteClick(qr.id)}
                  {...qr}
                />
              ))}
            </tbody>
          </table>
        ) : (
          <div className="bg-white p-10">
            {/* @ts-ignore */}
            <NoItems
              itemName="QR Code"
              plural="QR Codes"
              onNewClick={() => {
                setCreateQRCodeRedirectOpen(true);
              }}
            />
          </div>
        )}
      </div>
    </div>
  </>;
}